import React, { FC, useState } from 'react'
import { useField, useFormikContext } from 'formik'
import { DateInput } from '@babylon/web-platform-ui'

import { withValidationFields } from '../Forms/withField'
import { isValidDate, getISOFormattedDate } from '@/utils'

const DateInputWithValidationFields = withValidationFields(DateInput)

interface DateInputWithFieldProps {
  validate?: (value: string) => Promise<string | undefined>
  validateMessage?: string
  required?: boolean
  name?: string
  dayLabel: string
  monthLabel: string
  yearLabel: string
  locale: string
}

const DateInputWithField: FC<DateInputWithFieldProps> = ({
  name = 'date',
  validate,
  required,
  validateMessage,
  ...props
}: DateInputWithFieldProps) => {
  const [
    field,
    { error, touched },
    { setValue, setTouched },
  ] = useField<string>({
    name,
    validate: async (newValue) => {
      if (validate) {
        const validationError = await validate(newValue)

        if (validationError) {
          return validationError
        }
      }

      if ((required || touched) && (!newValue || !isValidDate(newValue))) {
        return validateMessage
      }

      return undefined
    },
  })

  const [touchedFieldIds, setTouchedFieldIds] = useState<string[]>([])

  const { status = {} } = useFormikContext()
  const fieldStatus: string[] | undefined = status[name]
  const hasStatus = fieldStatus && fieldStatus.length > 0
  const hasError = Boolean(touched && (error || hasStatus))

  const fieldErrors: string[] = []

  const setFieldTouched = (fieldId: string) => {
    if (!touchedFieldIds.includes(fieldId)) {
      touchedFieldIds.push(fieldId)
      setTouchedFieldIds(touchedFieldIds)
    }
  }

  const isTouched = () => touchedFieldIds.length === 3

  if (hasError) {
    if (error) {
      fieldErrors.push(error)
    } else if (hasStatus) {
      fieldErrors.push(...(fieldStatus as string[]))
    }
  }

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFieldTouched(e.currentTarget.id)

    if (isTouched()) {
      setTouched(true)
    }
  }

  const onDateChange = (date: string): void => {
    if (isTouched()) {
      setTouched(true)
    }

    if (isValidDate(date)) {
      setValue(getISOFormattedDate(date))
    } else {
      setValue(date)
    }
  }

  return (
    <DateInputWithValidationFields
      {...props}
      name={name}
      field={field}
      onChange={onDateChange}
      onBlur={onBlur}
      error={hasError}
      errorMessage={fieldErrors.join(' ')}
    />
  )
}

export default DateInputWithField
